import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavRail, BottomBar, Brand, NavItem as UINavItem } from './ui';
import {
  MessageSquare,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Twitter,
  Users,
  Building2,
  TrendingUp,
  ListChecks,
  LayoutDashboard,
  Clock,
  AlertCircle,
  MapPin,
  Ticket,
  History,
  User,
  Menu,
  Settings,
  LogOut,
  Edit,
  ChevronDown,
  Search,
  Globe,
  Bot,
  UserPlus,
  ExternalLink,
  X,
  Receipt,
  Target,
  FileText,
  Home
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { InferenceStatusBanner } from './InferenceStatusBanner';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/AuthContext';
import { LanguageSelector } from './i18n/LanguageSelector';
import { CustomerDetails } from './CustomerDetails';
import { LeadsGrid } from './crm-grid/LeadsGrid';
import { ContactsGrid } from './crm-grid/ContactsGrid';
import { ContactCreateForm } from './crm-grid/ContactCreateForm';
import { UndoToast } from './crm-grid/UndoToast';
import { AccountsGrid } from './crm-grid/AccountsGrid';
import { AccountDetailPanel, CompanyDetail } from './crm-grid/AccountDetailPanel';
import { AccountRow } from './crm-grid/AccountRows';
import { PipelineGrid } from './crm-grid/PipelineGrid';
import { DealRow } from './crm-grid/DealRows';
import { DealDetailPanel, DealDetail } from './crm-grid/DealDetailPanel';
import { InvoicesGrid } from './crm-grid/InvoicesGrid';
import { InvoiceRow } from './crm-grid/InvoiceRows';
import { InvoiceDetailPanel, InvoiceDetail } from './crm-grid/InvoiceDetailPanel';
import { TicketsGrid } from './crm-grid/TicketsGrid';
import { TicketRow } from './crm-grid/TicketRows';
import { TicketDetailPanel, TicketDetail } from './crm-grid/TicketDetailPanel';
import { GoalsBoard } from './crm-grid/GoalsBoard';
import { PortfolioTab } from './crm-grid/PortfolioTab';
import { DocumentsBoard } from './crm-grid/DocumentsBoard';
import { TaskBoard } from './crm-grid/TaskBoard';
import { Dashboard } from './crm-grid/Dashboard';
import { WaConversationsGrid } from './wa-grid/WaConversationsGrid';
import { WaRow, WaOwnerChip } from './wa-grid/waRows';

/**
 * 🔴 `agentId` IS POLYMORPHIC — it holds a human user id, a VIRTUAL-AGENT id ("va-growth-1"), or null.
 * Reading it as a boolean ("someone owns this") would file a BOT-handled conversation under "Míos" and
 * hide it from "Sin asignar", which is precisely backwards: a bot-handled chat is the unclaimed work a
 * human still needs to pick up. Only a non-virtual id counts as a human owner.
 */
const humanAgentId = (id: string | null | undefined): string | null =>
  id && !id.startsWith('va-') ? id : null;

/**
 * 🔴 OWNER FOR A WEB ROW — human, bot, or nobody, decided EXPLICITLY. Two traps here, and this codebase
 * has already paid for both of them once:
 *
 *  - Never read `agentId` as a boolean. `humanManaged = (agentId != null)` calls a BOT-handled chat
 *    human-managed, which files the unclaimed work under "Míos" and hides it from "Sin asignar".
 *  - Never let the `va-` prefix be the PRIMARY signal. It only holds for the four hardcoded SEED agents;
 *    a UI-published VirtualAgent gets a UUID (`VirtualAgent.id @default(uuid())`), so prefix-sniffing
 *    misreads its reply as a human takeover. The server hit exactly this and fixed it by having the
 *    CALLER state `isVirtual` (VisitorManager:1069-1078) — `chatStatus.isHumanManaged` IS that
 *    authoritative answer, and the server replays it on every console join. Prefer it; the prefix check
 *    survives only as the fallback for a chat assigned to a human who has not spoken yet.
 */
const webOwner = (cs: Visitor['chatStatus']): WaOwnerChip | null => {
  if (!cs) return null;
  // The server states this explicitly, three-valued: true = a human replied, false = a bot replied,
  // undefined = nobody has spoken yet. Only the third case needs a guess.
  if (cs.isHumanManaged === true) return { kind: 'human', name: null, agentId: cs.agentId ?? null };
  if (cs.isHumanManaged === false) return { kind: 'bot', name: null, agentId: null };
  if (!cs.agentId) return null;                          // nobody holds it
  return humanAgentId(cs.agentId)
    ? { kind: 'human', name: null, agentId: cs.agentId } // assigned to a human who has not replied yet
    : { kind: 'bot', name: null, agentId: null };
};

export interface Chat {
  id: string;
  customer: string;
  channel: string;
  // WhatsApp only (L2): a human agent has taken this conversation over (Conversation.agentId set). Drives
  // the "Hand back to bot" action — clearing it lets the bot auto-answer the next inbound.
  humanManaged?: boolean;
  status: string;
  waitTime: string;
  priority: string;
  preview: string;
  unread: boolean;
  location: {
    country: string;
    city: string;
    phone: string;
  };
  previousChats: {
    date: string;
    topic: string;
    status: string;
    messages: Message[];
    survey?: ChatSurvey;
  }[];
  activeCases: {
    id: string;
    topic: string;
    status: string;
    created: string;
  }[];
  ended?: EndedChat;
}

export interface Message {
  id: string;
  from: string;
  content: string;
  timestamp: string;
  // WhatsApp only: the outbound result for THIS reply. Absent/'sent' = no badge (web chat never sets
  // this). 'refused'/'error' means the console showed the reply as generated, but it did not reach the
  // customer — a wa-delivery incident found the console had no way to tell these apart from a real send.
  deliveryStatus?: 'sent' | 'refused' | 'error';
}
export interface ChatMessages {
  [chatId: string]: Message[];
}

export interface ChatSurvey {
  status: 'resolved' | 'pending' | 'escalated';
  description: string;
}

export interface EndedChat {
  endedAt: string;
  survey: ChatSurvey;
}

export interface PageVisit {
  path: string;
  timestamp: string;
  timeSpent: string;
}

export interface Visitor {
  id: string;
  currentPage: string;
  name: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  timeInPage: string;
  startedAt: string;
  ip: string;
  userAgent: {
    browser: string;
    version: string;
    os: string;
    device: string;
    language: string;
    platform: string;
    screenResolution: string;
    windowSize: string;
    colorDepth: number;
    touchScreen: boolean;
    cookiesEnabled: boolean;
    doNotTrack: boolean | null;
  };
  location?: {
    country: string;
    countryCode: string;
    city: string;
    region: string;
    timezone: string;
    flag?: string;
  };
  chatStatus?: {
    inChat: boolean;
    waiting: boolean;
    agentId?: string;
    lastMessage?: string;
    unread?: boolean;
    messages?: Message[];
    /** Set by the server only when a HUMAN agent replies (VisitorManager passes `isVirtual` from the
     *  caller), so it is the authoritative bot-vs-human answer — see `webOwner`. Arrives on every join. */
    isHumanManaged?: boolean;
  };
  navigationHistory: PageVisit[];
  performance?: {
    loadTime: number;
    domContentLoaded: number;
    firstPaint: number;
  };
  referrer: string;
  isCustomer?: boolean;
  notes?: string;
  tags?: string[];
  conversationsCount?: number;
  lastConversationAt?: string;
  // CRM ROLODEX (additive): fields the rolodex (Lead) read surfaces. `id` is a representative anchor
  // token for back-compat; leadId is the durable record. qualificationReason drives the AC11a badge;
  // alternateContacts/mergeLog drive the merge disclosure surfaces (AC15).
  leadId?: string;
  qualificationReason?: 'save' | 'contact' | 'conversation' | null;
  alternateContacts?: Array<{ kind: string; value: string; fromLeadId?: string }>;
  mergeLog?: Array<{ fromLeadId: string; broughtIn?: { conversations: number; tags: number; alternateContacts: number }; listedBefore?: boolean; listedAfter?: boolean; undone?: boolean }>;
  // All of the lead's anchor tokens (rolodex payload), so the live-chat sidebar can resolve a merged
  // lead chatted on a non-oldest anchor to its Customers-list record (F1/F2 completeness).
  anchorTokens?: string[];
}

export interface Note {
  id: string;
  content: string;
  agentId: string;
  agentName: string;
  createdAt: string;
}

type ActiveTab = 'today' | 'visitors' | 'conversations' | 'customers' | 'contacts' | 'accounts' | 'pipeline' | 'invoices' | 'tickets' | 'goals' | 'documents' | 'tasks' | 'portfolio' | 'settings';

const VisitorsTab: React.FC<{
  visitors: Visitor[],
  onTakeChat: (visitorId: string) => void,
  onSaveCustomer: (visitorId: string) => void,
  onProactiveChat: (visitorId: string, content: string) => void
}> = ({ visitors, onTakeChat, onSaveCustomer, onProactiveChat }) => {
  const { t } = useTranslation('common');
  const [proactiveMsg, setProactiveMsg] = useState<{ id: string, text: string } | null>(null);

  return (
    <div className="w-full h-full" data-testid="visitors-tab">
      <table className="min-w-full">
        <thead>
          <tr className="bg-canvas">
            <th data-testid="visitor-header" className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.visitor')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.ip')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.city')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.country')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.flag')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.timeInPage')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.lastMessage')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.currentPage')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.chatStatus')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('visitors.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-border-neutral">
          {visitors.map((visitor) => {
            const timeInPage = visitor.timeInPage || '0s';
            console.log(`DEBUG-RENDER: Visitor ${visitor.id} state:`, JSON.stringify(visitor.chatStatus));

            return (
              <tr key={visitor.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="text-sm font-medium text-text-strong">
                        {visitor.name || visitor.id.substring(0, 8)}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                  {visitor.ip || '127.0.0.1'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                  {visitor.location?.city || 'Unknown'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                  {visitor.location?.country || 'Unknown'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <img
                    src={visitor.location?.flag ?? 'https://flagcdn.com/24x18/un.png'}
                    alt={visitor.location?.countryCode ?? 'UN'}
                    className="w-5 h-4 object-cover"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                  {timeInPage}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                  {visitor.chatStatus?.lastMessage ? (
                    <div className="max-w-xs truncate" title={visitor.chatStatus.lastMessage}>
                      {visitor.chatStatus.lastMessage}
                    </div>
                  ) : (
                    <span className="text-text-muted">{t('visitors.noMessages')}</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-text-muted">
                  <div className="max-w-xs break-all">
                    {visitor.currentPage}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {visitor.chatStatus?.waiting && (
                    <div className="flex items-center text-green-500 gap-1">
                      <div className="animate-pulse w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-xs font-semibold uppercase">{t('visitors.status.waiting')}</span>
                    </div>
                  )}
                  {visitor.chatStatus?.inChat && (
                    <div className="flex items-center text-primary gap-1">
                      <div className="w-2 h-2 bg-primary rounded-full" />
                      <span className="text-xs font-semibold uppercase">{t('visitors.status.active')}</span>
                    </div>
                  )}
                  {!visitor.chatStatus?.waiting && !visitor.chatStatus?.inChat && (
                    <span className="text-xs text-text-muted uppercase">{t('visitors.status.monitoring')}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    {(visitor.chatStatus?.waiting || ((visitor.chatStatus?.messages?.length ?? 0) > 0 && !visitor.chatStatus?.inChat)) ? (
                      <button
                        onClick={() => onTakeChat(visitor.id)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs font-bold"
                      >
                        {t('visitors.btns.take')}
                      </button>
                    ) : !visitor.chatStatus?.inChat ? (
                      <button
                        data-testid={`proactive-trigger-${visitor.id}`}
                        onClick={() => setProactiveMsg({ id: visitor.id, text: '' })}
                        className="px-3 py-1 bg-primary text-white rounded hover:bg-primary text-xs font-bold"
                      >
                        {t('visitors.btns.chat')}
                      </button>
                    ) : (
                      <button
                        onClick={() => onTakeChat(visitor.id)}
                        className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-bold"
                      >
                        {t('visitors.btns.open')}
                      </button>
                    )}

                    {!visitor.isCustomer && (
                      <button
                        onClick={() => onSaveCustomer(visitor.id)}
                        className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-canvas text-xs font-bold"
                      >
                        {t('visitors.btns.save')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {proactiveMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-black">{t('visitors.proactive.title')}</h2>
            <p className="text-xs text-text-muted mb-4">{t('visitors.proactive.subtitle')}</p>
            <textarea
              data-testid="proactive-input"
              autoFocus
              value={proactiveMsg.text}
              onChange={(e) => setProactiveMsg({ ...proactiveMsg, text: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg h-32 text-black text-sm"
              placeholder={t('visitors.proactive.placeholder')}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setProactiveMsg(null)}
                className="px-4 py-2 text-gray-600 hover:bg-canvas rounded-lg text-sm font-semibold"
              >
                {t('actions.cancel')}
              </button>
              <button
                data-testid="proactive-send"
                onClick={() => {
                  if (proactiveMsg.text.trim()) {
                    onProactiveChat(proactiveMsg.id, proactiveMsg.text);
                    setProactiveMsg(null);
                  }
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-semibold"
              >
                {t('visitors.proactive.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomersTab: React.FC<{
  kind?: 'lead' | 'contact';
  customers: Visitor[];
  onSelectCustomer: (v: Visitor | null) => void;
  selectedCustomer: Chat | null;
  onViewPastChat: (chat: Chat['previousChats'][0]) => void;
  onRemoveLead: (id: string) => void;
  visitors: Visitor[];
  onUpdateContact: (contact: { firstName?: string, lastName?: string, phone?: string, email?: string }) => void;
  onUpdateTags: (tags: string[]) => void;
  onAddNote: (content: string) => Promise<Note>;
  notes: Note[];
  onContactCreated: (leadId: string) => void;
}> = ({ kind = 'lead', customers, onSelectCustomer, selectedCustomer, onViewPastChat, onRemoveLead, visitors, onUpdateContact, onUpdateTags, onAddNote, notes, onContactCreated }) => {
  const { t } = useTranslation('common');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);
  // pocketeam#84 — a minimal, VISITOR-shaped stand-in built from a grid row (ContactRow/LeadRow), for
  // the ONE case below where the real lookup misses. CAVEAT-1, verified by reading CustomerDetails:
  // before its OWN personId-keyed detail fetch resolves, only PanelSkeleton renders (zero props, a
  // static placeholder) — displayName/channelLabel (the only things read off `chat`) live inside
  // `{detail && (...)}` and never render pre-load; `chat` itself is built by handleSelectCustomer's
  // own chatProxy, which already defaults `customer` via `v.name || v.id.substring(0,8)`; the
  // SEPARATE `visitor` prop (live-visitor enrichment, a different lookup entirely) is read only via
  // `visitor?.navigationHistory` etc., already optional-chained. {id, name} is everything needed —
  // nothing here can throw.
  const makeVisitorFromRow = (row: { id: string; name?: string }): Visitor =>
    ({ id: row.id, name: row.name ?? null } as Visitor);

  const openPerson = (row: { id: string; name?: string }) => {
    // ORIGINAL lookup FIRST, UNCHANGED — an existing successful match still passes the real, richer
    // Visitor byte-for-byte (zero behavior change for the existing leads/visitor flow). The fallback
    // fires ONLY on the case that used to be a silent no-op: ContactsGrid/LeadsGrid already fetched
    // and rendered this exact row (their own, separately-issued fetch to the SAME endpoint the
    // parent's `customers` state also reads), so a row can be clickable on screen before that parent
    // fetch lands — the lookup misses, and without this fallback the click did nothing: no panel, no
    // error, no visible sign anything happened. Also the mechanism behind the pre-existing prod
    // tap-a-lead race (same shared handler), not Contacts-specific.
    const v = customers.find(c => c.id === row.id) || visitors.find(vi => vi.id === row.id) || makeVisitorFromRow(row);
    if (v) onSelectCustomer(v);
  };
  const closePanel = () => { onSelectCustomer(null); setIsCreatingContact(false); };
  const handleCreated = (leadId: string) => {
    setIsCreatingContact(false);
    setContactsRefreshKey((k) => k + 1);
    onContactCreated(leadId); // refreshes the OUTER customers list + selects the new row (same panel slot flips to CustomerDetails)
  };
  const panelOpen = isCreatingContact || !!selectedCustomer;

  // #62 Contacts depth spec §3 DELETE — "Otherwise delete -> UndoToast" (same ratified pattern as
  // Task's optimistic-write undo). Undo reuses the EXISTING save endpoint (LeadService.saveLead
  // reverses removeLead's suppression, "reversible by an explicit save" — same contract the Leads
  // screen's own remove/save pair already relies on).
  const [deleteToast, setDeleteToast] = useState<{ leadId: string } | null>(null);
  const handleContactDeleted = (leadId: string) => {
    closePanel();
    setContactsRefreshKey((k) => k + 1);
    setDeleteToast({ leadId });
  };
  const handleUndoDelete = async () => {
    if (!deleteToast) return;
    const leadId = deleteToast.leadId;
    setDeleteToast(null);
    await fetch(`/api/customers/${leadId}/save`, { method: 'POST' });
    setContactsRefreshKey((k) => k + 1);
  };

    return (
      <div className="flex flex-1 overflow-hidden">
        {/* Left side - Search and Lists */}
        <div className="flex-1 bg-white flex flex-col min-w-0">
          {/* #62 Contacts depth spec §3 CREATE — header action, applying the Tasks-loop shape
              (minimal-create-then-enrich). Leads has no create flow yet (out of this round's scope). */}
          {kind === 'contact' && (
            <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
              <button type="button" data-testid="contact-new" aria-label={t('contactsGrid.newContact')}
                onClick={() => { onSelectCustomer(null); setIsCreatingContact(true); }}
                className="inline-flex items-center justify-center gap-1.5 h-9 min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
                <span aria-hidden>+</span>
                <span className="hidden md:inline">{t('contactsGrid.newContact')}</span>
              </button>
            </div>
          )}

          {/* P2/#62 CRM-grid — cards on phone/tablet, table on desktop. Full population from /api/customers
              via getRolodex (Leads = kind=lead, Contacts = kind=contact — the SAME table, D3 shape (a)). */}
          <div className={`flex-1 overflow-y-auto ${panelOpen ? 'hidden lg:block' : 'block'}`}>
            {kind === 'contact' ? (
              <ContactsGrid selectedId={selectedCustomer?.id} onOpenPerson={openPerson} refreshKey={contactsRefreshKey} />
            ) : (
              <LeadsGrid selectedId={selectedCustomer?.id} onRemove={onRemoveLead} onOpenPerson={openPerson} refreshKey={contactsRefreshKey}
          onConverted={() => setContactsRefreshKey((k) => k + 1)} />
            )}
          </div>
        </div>

        {/* Right side — CustomerDetails OR the create form, same panel slot (#62: create->detail is one
            continuous flow, never a second panel). ONLY when open — grid is full-width + primary with
            nothing selected (no empty panel, no table h-scroll). Right panel on lg:, full-screen on smaller. */}
        <div className={`bg-canvas flex-col h-full overflow-hidden border-s border-border-neutral w-full lg:w-96 lg:shrink-0 ${panelOpen ? 'flex fixed inset-0 z-40 lg:static lg:inset-auto' : 'hidden'}`}>
          {isCreatingContact ? (
            <>
              <button data-testid="contact-create-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 text-sm text-primary font-semibold" onClick={closePanel}>&larr; {t('contactsGrid.backToGrid')}</button>
              <ContactCreateForm onCreated={handleCreated} onCancel={closePanel} />
            </>
          ) : selectedCustomer ? (
            <>
              {/* Mobile: back to the grid (E-3 full-screen detail on <lg). Hidden on lg: where the grid stays visible. */}
              <button className="lg:hidden self-start flex items-center gap-1 px-4 py-3 text-sm text-primary font-semibold" onClick={closePanel}>&larr; Back to grid</button>
            <CustomerDetails
              // ONE SUBJECT (PRD §1 / design §2): personId is the stable identity token, resolved
              // server-side by Lead identity (GET /api/customers/:id/detail → resolveLeadId) — never by
              // live-roster presence. `visitor` is OPTIONAL live enrichment ONLY.
              personId={selectedCustomer.id}
              visitor={visitors.find(v => v.id === selectedCustomer.id)}
              chat={selectedCustomer}
              onViewPastChat={onViewPastChat}
              onUpdateContact={onUpdateContact}
              onUpdateTags={onUpdateTags}
              onAddNote={onAddNote}
              notes={notes}
              // #62 Contacts depth spec §3 DELETE — Contacts only this round; Leads keeps its existing
              // in-grid remove affordance (a different, already-shipped pattern) rather than gaining a
              // second, redundant delete control.
              onDeleted={kind === 'contact' ? () => handleContactDeleted(selectedCustomer.id) : undefined}
              // B5 — a convert from the detail moves the person between lists; both grids share
              // contactsRefreshKey, so one bump teaches whichever list is mounted.
              onConverted={() => setContactsRefreshKey((k) => k + 1)}
            />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-text-muted">
              <Users className="w-16 h-12 mb-4 opacity-20" />
              <p className="text-lg font-medium">Select a customer to view details</p>
              <p className="text-sm">Manage your CRM records and view past interactions.</p>
            </div>
          )}
        </div>
        {deleteToast && (
          <UndoToast message={t('contactsGrid.deleted')} variant="undo" onUndo={handleUndoDelete} onExpire={() => setDeleteToast(null)} />
        )}
      </div>
    );
  };

// #62 Phase 2 — Accounts screen (UXeng's per-screen spec §3). Company has no field-editing spec yet, so
// the detail panel is read-only for this pass (presence≠function: a row that opens SOMETHING real, not
// a fabricated edit surface). Same E-3 overlay shell as CustomersTab (side panel on lg:, full-screen
// overlay below it) for consistency, but its own lightweight content — an AccountRow isn't Lead-shaped.
const AccountsTab: React.FC<{ onNavigateToPortfolio?: () => void }> = ({ onNavigateToPortfolio }) => {
  const { t } = useTranslation('common');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [survivorToast, setSurvivorToast] = useState<string | null>(null);
  const panelOpen = isCreating || !!selectedId;
  const closePanel = () => { setSelectedId(null); setIsCreating(false); };

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 bg-white flex flex-col min-w-0">
        {/* #62 Accounts depth spec §4 CREATE — header action, Tasks-loop shape. */}
        <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
          <button type="button" data-testid="account-new" aria-label={t('accountsGrid.newAccount')}
            onClick={() => { setSelectedId(null); setIsCreating(true); }}
            className="inline-flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
            <span aria-hidden>+</span>
            <span className="hidden md:inline">{t('accountsGrid.newAccount')}</span>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto ${panelOpen ? 'hidden lg:block' : 'block'}`}>
          <AccountsGrid selectedId={selectedId} onOpenAccount={(a) => { setIsCreating(false); setSelectedId(a.id); }} refreshKey={refreshKey}
            onCreate={() => { setSelectedId(null); setIsCreating(true); }} />
        </div>
      </div>
      <div className={`bg-canvas flex-col h-full overflow-hidden border-s border-border-neutral w-full lg:w-96 lg:shrink-0 ${panelOpen ? 'flex fixed inset-0 z-40 lg:static lg:inset-auto' : 'hidden'}`}>
        {panelOpen ? (
          <AccountDetailPanel
            companyId={isCreating ? 'new' : (selectedId as string)}
            sheetShape={isMobile ? 'sheet' : 'popover'}
            onClose={closePanel}
            onCreated={(c: CompanyDetail) => { setIsCreating(false); setSelectedId(c.id); setRefreshKey((k) => k + 1); }}
            onUpdated={() => setRefreshKey((k) => k + 1)}
            onNavigateToPortfolio={onNavigateToPortfolio}
            onDeleted={(_id, unlinked) => {
              closePanel();
              setRefreshKey((k) => k + 1);
              const parts = [
                unlinked.contacts > 0 ? t('accountsGrid.survivorContacts', { count: unlinked.contacts }) : null,
                unlinked.deals > 0 ? t('accountsGrid.survivorDeals', { count: unlinked.deals }) : null,
                // B9 — tasks and tickets survive the delete too, and the toast named neither.
                unlinked.tasks > 0 ? t('accountsGrid.survivorTasks', { count: unlinked.tasks }) : null,
                unlinked.tickets > 0 ? t('accountsGrid.survivorTickets', { count: unlinked.tickets }) : null,
                unlinked.activities > 0 ? t('accountsGrid.survivorActivities', { count: unlinked.activities }) : null,
              ].filter(Boolean);
              setSurvivorToast(parts.length ? t('accountsGrid.deletedWithSurvivors', { parts: parts.join(', ') }) : t('accountsGrid.deleted'));
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <Users className="w-16 h-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{t('accountsGrid.selectPrompt')}</p>
          </div>
        )}
      </div>
      {survivorToast && (
        <UndoToast message={survivorToast} onExpire={() => setSurvivorToast(null)} />
      )}
    </div>
  );
};

// #62 Pipeline-complete-loop (cmdr/PM, 2026-08-23): create/detail/edit/close/delete, Tasks-loop shape.
// Deal's field-editing (stage/owner/value via ValueSheet, name inline) lives in DealDetailPanel.
const PipelineTab: React.FC = () => {
  const { t } = useTranslation('common');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const panelOpen = isCreating || !!selectedId;
  const closePanel = () => { setSelectedId(null); setIsCreating(false); };
  // D-D7 — disclose what SURVIVED after a deal delete (tasks kept, and where they went), mirroring
  // the accounts tab's survivor toast. The confirm said what WILL happen; this says what DID.
  const [survivorToast, setSurvivorToast] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 bg-white flex flex-col min-w-0">
        <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
          <button type="button" data-testid="deal-new" aria-label={t('pipelineGrid.newDeal')}
            onClick={() => { setSelectedId(null); setIsCreating(true); }}
            className="inline-flex items-center justify-center gap-1.5 h-9 min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
            <span aria-hidden>+</span>
            <span className="hidden md:inline">{t('pipelineGrid.newDeal')}</span>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto ${panelOpen ? 'hidden lg:block' : 'block'}`}>
          <PipelineGrid selectedId={selectedId} onOpenDeal={(d: DealRow) => { setIsCreating(false); setSelectedId(d.id); }} refreshKey={refreshKey} />
        </div>
      </div>
      <div className={`bg-canvas flex-col h-full overflow-hidden border-s border-border-neutral w-full lg:w-96 lg:shrink-0 ${panelOpen ? 'flex fixed inset-0 z-40 lg:static lg:inset-auto' : 'hidden'}`}>
        {panelOpen ? (
          <DealDetailPanel
            dealId={isCreating ? 'new' : (selectedId as string)}
            sheetShape={isMobile ? 'sheet' : 'popover'}
            onClose={closePanel}
            onCreated={(d: DealDetail) => { setIsCreating(false); setSelectedId(d.id); setRefreshKey((k) => k + 1); }}
            onUpdated={() => setRefreshKey((k) => k + 1)}
            onDeleted={(_id, survivors) => {
              closePanel();
              setRefreshKey((k) => k + 1);
              setSurvivorToast(survivors.reparentedTasks > 0
                ? (survivors.companyName
                  ? t('pipelineGrid.deletedTasksKept', { count: survivors.reparentedTasks, company: survivors.companyName })
                  : t('pipelineGrid.deletedTasksKeptNoCompany', { count: survivors.reparentedTasks }))
                : t('pipelineGrid.deleted'));
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <Users className="w-16 h-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{t('pipelineGrid.selectPrompt')}</p>
          </div>
        )}
      </div>
      {survivorToast && (
        <UndoToast message={survivorToast} onExpire={() => setSurvivorToast(null)} />
      )}
    </div>
  );
};

const TicketsTab: React.FC = () => {
  const { t } = useTranslation('common');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const panelOpen = isCreating || !!selectedId;
  const closePanel = () => { setSelectedId(null); setIsCreating(false); };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 bg-white flex flex-col min-w-0">
        <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
          <button type="button" data-testid="ticket-new" aria-label={t('ticketsGrid.newTicket')}
            onClick={() => { setSelectedId(null); setIsCreating(true); }}
            className="inline-flex items-center justify-center gap-1.5 h-9 min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
            <span aria-hidden>+</span>
            <span className="hidden md:inline">{t('ticketsGrid.newTicket')}</span>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto ${panelOpen ? 'hidden lg:block' : 'block'}`}>
          <TicketsGrid selectedId={selectedId} onOpenTicket={(tk: TicketRow) => { setIsCreating(false); setSelectedId(tk.id); }} refreshKey={refreshKey} />
        </div>
      </div>
      <div className={`bg-canvas flex-col h-full overflow-hidden border-s border-border-neutral w-full lg:w-96 lg:shrink-0 ${panelOpen ? 'flex fixed inset-0 z-40 lg:static lg:inset-auto' : 'hidden'}`}>
        {panelOpen ? (
          <TicketDetailPanel
            ticketId={isCreating ? 'new' : (selectedId as string)}
            sheetShape={isMobile ? 'sheet' : 'popover'}
            onClose={closePanel}
            onCreated={(tk: TicketDetail) => { setIsCreating(false); setSelectedId(tk.id); setRefreshKey((k) => k + 1); }}
            onUpdated={() => setRefreshKey((k) => k + 1)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <Users className="w-16 h-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{t('ticketsGrid.selectPrompt')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const InvoicesTab: React.FC = () => {
  const { t } = useTranslation('common');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const panelOpen = isCreating || !!selectedId;
  const closePanel = () => { setSelectedId(null); setIsCreating(false); };

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 bg-white flex flex-col min-w-0">
        <div className="flex justify-end px-3 pt-2 lg:px-4 lg:pt-3">
          <button type="button" data-testid="invoice-new" aria-label={t('invoicesGrid.newInvoice')}
            onClick={() => { setSelectedId(null); setIsCreating(true); }}
            className="inline-flex items-center justify-center gap-1.5 h-9 min-w-[44px] px-3 rounded-lg bg-primary text-white text-sm font-semibold">
            <span aria-hidden>+</span>
            <span className="hidden md:inline">{t('invoicesGrid.newInvoice')}</span>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto ${panelOpen ? 'hidden lg:block' : 'block'}`}>
          <InvoicesGrid selectedId={selectedId} onOpenInvoice={(i: InvoiceRow) => { setIsCreating(false); setSelectedId(i.id); }} refreshKey={refreshKey} />
        </div>
      </div>
      <div className={`bg-canvas flex-col h-full overflow-hidden border-s border-border-neutral w-full lg:w-96 lg:shrink-0 ${panelOpen ? 'flex fixed inset-0 z-40 lg:static lg:inset-auto' : 'hidden'}`}>
        {panelOpen ? (
          <InvoiceDetailPanel
            invoiceId={isCreating ? 'new' : (selectedId as string)}
            onClose={closePanel}
            onCreated={(i: InvoiceDetail) => { setIsCreating(false); setSelectedId(i.id); setRefreshKey((k) => k + 1); }}
            onUpdated={() => setRefreshKey((k) => k + 1)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <Users className="w-16 h-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{t('invoicesGrid.selectPrompt')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

import { VirtualizationTab } from './backoffice/pages/VirtualizationTab';
import { PipelineSettings } from './crm-grid/PipelineSettings';
import { widgetSnippet } from '../widget/snippet';
import { resolveMegaUrl } from '../utils/megaIframeUrl';

const SettingsTab: React.FC = () => {
  const { t } = useTranslation('common');
  const { accountId } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState<'general' | 'virtual' | 'pipeline'>('general');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch('/api/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        // H-12 — /api/invite creates an Invitation row and a token. It MAILS NOTHING (the route
        // still carries "In a real app, send an email here"). The capability is fine: the inviter gets
        // a working join link right here. The WORD was the defect — "Invitation sent" told the tenant
        // a colleague had been contacted, so they stop chasing, or tell that colleague to check their
        // spam. It now says what happened and whose job the sending is.
        setStatus({ type: 'success', message: `${t('settings.team.linkCreated')} ${window.location.origin}/join/${data.token}` });
        setEmail('');
      } else {
        setStatus({ type: 'error', message: data.error || t('settings.team.createFailed') });
      }
    } catch (err) {
      setStatus({ type: 'error', message: t('settings.team.networkError') });
    } finally {
      setLoading(false);
    }
  };

  const testPageUrl = `${window.location.origin}/test.html?accountId=${accountId}`;

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      <div className="p-6 border-b border-border-neutral">
        <h2 className="text-2xl font-bold text-black">{t('settings.title')}</h2>
        <div className="flex gap-4 mt-4">
          <button
            onClick={() => setActiveSubTab('general')}
            className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${activeSubTab === 'general' ? 'border-blue-600 text-primary' : 'border-transparent text-text-muted hover:text-gray-600'}`}
          >
            General
          </button>
          <button
            onClick={() => setActiveSubTab('virtual')}
            className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${activeSubTab === 'virtual' ? 'border-blue-600 text-primary' : 'border-transparent text-text-muted hover:text-gray-600'}`}
          >
            Virtual Agents
          </button>
          <button
            data-testid="settings-subtab-pipeline"
            onClick={() => setActiveSubTab('pipeline')}
            className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${activeSubTab === 'pipeline' ? 'border-blue-600 text-primary' : 'border-transparent text-text-muted hover:text-gray-600'}`}
          >
            {t('settings.pipeline.tab')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {activeSubTab === 'general' ? (
          <div className="max-w-2xl mx-auto w-full space-y-6">
            {/* Integration Section */}
            <div className="bg-white rounded-2xl border border-border-neutral p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-black">
                <Globe className="w-5 h-5 text-primary" />
                Chat Widget Integration
              </h3>
              <p className="text-text-muted mb-4 text-sm text-black">
                Use this link to test your chat widget in a simulated environment.
              </p>
              <div className="flex gap-2 items-center bg-canvas p-3 rounded-xl border border-border-neutral">
                <input
                  readOnly
                  value={testPageUrl}
                  className="flex-1 bg-transparent text-sm text-gray-600 outline-none"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(testPageUrl)}
                  className="text-primary text-sm font-semibold hover:text-blue-700"
                >
                  Copy Link
                </button>
              </div>
              <div className="mt-4 flex justify-end">
                <a
                  href={testPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm font-semibold hover:text-blue-700 flex items-center gap-1"
                >
                  Open Test Page <Globe className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Installation Code Section */}
            <div className="bg-white rounded-2xl border border-border-neutral p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-black">
                <code className="text-primary font-bold text-xl">{'</>'}</code>
                {t('settings.installation.title')}
              </h3>
              <p className="text-text-muted mb-4 text-sm text-black">
                {t('settings.installation.subtitle')}
              </p>
              <div className="relative">
                <textarea
                  readOnly
                  className="w-full h-24 bg-gray-900 text-gray-100 p-4 rounded-xl text-sm font-mono focus:outline-none resize-none"
                  value={widgetSnippet(window.location.origin, accountId)}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(widgetSnippet(window.location.origin, accountId))}
                  className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-lg text-xs font-semibold transition-colors backdrop-blur-sm"
                >
                  {t('settings.installation.copyBtn')}
                </button>
              </div>
            </div>

            {/* Team Members Section */}
            <div className="bg-white rounded-2xl border border-border-neutral p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-black">
                <Users className="w-5 h-5 text-primary" />
                {t('settings.team.title')}
              </h3>
              <p className="text-text-muted mb-6 text-sm text-black">
                {t('settings.team.subtitle')}
              </p>

              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.team.emailLabel')}</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-black"
                      placeholder={t('settings.team.emailPlaceholder')}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                    <button
                      disabled={loading}
                      type="submit"
                      className="bg-primary text-white px-6 py-2 rounded-xl font-semibold hover:bg-primary-dark transition-all disabled:opacity-50"
                    >
                      {loading ? t('settings.team.sending') : t('settings.team.inviteBtn')}
                    </button>
                  </div>
                </div>

                {status && (
                  <div className={`p-4 rounded-xl text-sm ${status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {status.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        ) : activeSubTab === 'pipeline' ? (
          <div className="max-w-2xl mx-auto w-full">
            {/* B4 — the pipeline's vocabulary is account CONFIGURATION, not a CRM screen (04-Stage §7.3) */}
            <PipelineSettings />
          </div>
        ) : (
          <div className="h-full">
            <VirtualizationTab />
          </div>
        )}
      </div>
    </div>
  );
};

const AgentConsole: React.FC = () => {
  const { t } = useTranslation('common');
  console.log('AgentConsole mounting');
  // Initialize with empty arrays - remove any mock data
  const { user, accountId, logout } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [socket, setSocket] = useState<WebSocket | null>(null);
  // Non-null while the last send attempt could not be transmitted. Cleared on a successful send and on
  // reconnect, so it reflects the CURRENT transport, not a stale scare.
  const [sendError, setSendError] = useState<string | null>(null);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [customers, setCustomers] = useState<Visitor[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('visitors');

  const fetchCustomers = async (kind: 'lead' | 'contact' = 'lead') => {
    try {
      const res = await fetch(`/api/customers?kind=${kind}`);
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
        return data as any[];
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
    return [] as any[];
  };

  // #62 Contacts depth spec §3 — after POST /api/customers creates a real Lead row, re-fetch the SAME
  // rolodex read every other contact row comes from (no synthetic Chat object to keep in sync by hand)
  // and select the new row through it, so CustomerDetails opens exactly as it does for a clicked row.
  const handleContactCreated = async (leadId: string) => {
    const rows = await fetchCustomers('contact');
    const row = rows.find((r) => r.id === leadId || r.leadId === leadId);
    if (row) handleSelectCustomer(row);
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // WhatsApp conversations (P2c-2) are DB-sourced (no live visitor socket), so the console fetches them
  // and merges them into `chats` as ADDITIVE entries with channel='whatsapp' (→ the green badge via
  // getChannelIcon). Web chats (from the live WebSocket) are never touched. Account-scoping is enforced
  // server-side (the endpoint filters by the session's accountId).
  // WA-2: the conversation the grid opened, pinned by CONVERSATION id. The inbox merge below is keyed by
  // VISITOR and only sees `status: 'open'` rows, so it must not overwrite the messages of a specific
  // conversation the operator opened from the grid (two conversations can share one contact — AC-W10).
  const [waThread, setWaThread] = useState<{ conversationId: string; visitorId: string } | null>(null);
  const waThreadRef = useRef<{ conversationId: string; visitorId: string } | null>(null);
  useEffect(() => { waThreadRef.current = waThread; }, [waThread]);

  /**
   * 🔴 WEB ROWS, WITH ENOUGH FACTS TO BE SORTED AND FILTERED LIKE EVERYTHING ELSE.
   *
   * They used to be emitted with `lastMessageAt: null`, `estado: null` and `owner: null` on the reasoning
   * that a web conversation has no WhatsApp lifecycle. True of the 24h window — and false of everything
   * else. A null timestamp meant a brand-new web chat could never sort by recency and was pinned below
   * every WhatsApp row; a null estado/owner meant every work-queue view had to drop it. "It has no
   * WhatsApp projection" quietly became "it is not really a conversation".
   *
   * ESTADO is a claim about who spoke last, and OWNER about who holds it — both are perfectly meaningful
   * for web, and every fact needed to answer them is already on the client:
   *   - the timestamp comes from the last message. The `Chat` interface itself carries NO timestamp
   *     (`waitTime` is a display string), so a recency sort keyed on the row would have had nothing to
   *     sort on and would have looked fixed while ordering arbitrarily.
   *   - the owner comes from the visitor's `chatStatus`, which the server replays on every join.
   *
   * 🔴 TWO SOURCES, AND `chats` IS READ-ONLY HERE.
   *
   * `chats` holds the conversations this console has ACCEPTED, and it starts EMPTY on every page load —
   * only `chat:assigned`, a live fire-and-forget event that is never replayed, adds to it. So after a
   * reload (or opening the console after a chat began) a real web conversation was simply ABSENT from the
   * grid — not buried, not unsorted, gone, with nothing for the sort to sort.
   *
   * The missing facts were already on the client and being ignored: `sendVisitorsToAgent`
   * (VisitorManager:687, called from `addAgentSocket` on every join) sends each visitor minus the socket,
   * so `chatStatus` — `messages` with per-message timestamps, `lastMessage`, `agentId`, `isHumanManaged` —
   * arrives on every connect. That is also what makes a RECONNECT restore the operator's screen, so no
   * durable re-read is needed for this.
   *
   * I first hydrated those visitors INTO `chats`, which was wrong and broke two specs. `chats` also feeds
   * the legacy conversation-list panes, so injecting not-yet-accepted visitors into it put extra rows —
   * and their message PREVIEWS — into a list that is `display:none` below `lg`. `cy.contains(text)` then
   * matched the hidden preview instead of the thread, and `chat`/`history` failed on an assertion that had
   * nothing to do with them. Deriving a second row source here instead leaves `chats` and the panes that
   * read it untouched.
   *
   * A roster-only row is still fully openable: `handleOpenGridRow` promotes it into `chats` on CLICK,
   * which is what keeps this list honest without paying the pollution cost up front.
   */
  const webRows = useMemo(() => {
    const statusByVisitor = new Map(visitors.map((v) => [v.id, v.chatStatus] as const));

    const row = (
      visitorId: string, contact: string, phone: string | null, preview: string,
      cs: Visitor['chatStatus'], msgs: Message[],
    ) => {
      const last = msgs.length ? msgs[msgs.length - 1] : null;
      const lastFrom: 'visitor' | 'agent' | null =
        last ? (last.from === 'agent' || last.from === 'agent-1' ? 'agent' : 'visitor') : null;
      return {
        key: `web:${visitorId}`, conversationId: null, visitorId, contact,
        phone, preview: preview || cs?.lastMessage || '',
        lastMessageAt: last?.timestamp ?? null,
        channel: 'web' as const,
        estado: lastFrom === 'visitor' ? ('necesita_respuesta' as const)
          : lastFrom === 'agent' ? ('esperando_al_cliente' as const) : null,
        owner: webOwner(cs),
        waitMs: null,
        windowExpiresAt: null, windowClosing: false, windowTier: 'none' as const, operationalStatus: null,
      };
    };

    // Accepted conversations. The locally-observed messages are freshest — they are what the operator
    // just watched land.
    const fromChats = chats.filter((c) => c.channel !== 'whatsapp').map((c) =>
      row(c.id, c.customer, c.location?.phone || null, c.preview, statusByVisitor.get(c.id), messages[c.id] ?? []));

    // Everything else the roster knows about: a chat that began before this console opened, or survived a
    // reload. Its messages come from the replayed `chatStatus`, which is the only copy we have of them.
    // Keyed on EVERY chat, not just the web ones: a roster entry must never produce a second row for an
    // id the console already holds, whatever channel it holds it under.
    const accepted = new Set(chats.map((c) => c.id));
    const fromRoster = visitors
      .filter((v) => {
        if (accepted.has(v.id)) return false;
        const cs = v.chatStatus;
        // Real chat activity only. A visitor merely browsing is not a conversation, and `endChat` clears
        // chatStatus server-side, so an ended chat cannot reappear here.
        return !!cs && ((cs.messages?.length ?? 0) > 0 || cs.inChat || cs.waiting);
      })
      // The same short id `chat:assigned` uses — a raw UUID in the contact column is unreadable.
      .map((v) => row(v.id, v.id.substring(0, 8), null, v.chatStatus?.lastMessage || '', v.chatStatus, v.chatStatus?.messages ?? []));

    return [...fromChats, ...fromRoster];
  }, [chats, visitors, messages]);

  const fetchWhatsAppConversations = async () => {
    try {
      const res = await fetch('/api/whatsapp/conversations');
      if (!res.ok) return;
      const data = await res.json();
      const convs: Array<{ id: string; customer: string; phone: string; lastMessage: string; humanManaged: boolean; messages: Message[] }> = data.conversations ?? [];
      setChats(prev => {
        const web = prev.filter(c => c.channel !== 'whatsapp');
        const wa: Chat[] = convs.map(cv => ({
          id: cv.id,
          customer: cv.customer,
          channel: 'whatsapp',
          humanManaged: cv.humanManaged,
          status: 'active',
          waitTime: '',
          priority: 'medium',
          preview: cv.lastMessage,
          unread: false,
          location: { country: '', city: '', phone: cv.phone },
          previousChats: [],
          activeCases: [],
        }));
        return [...web, ...wa];
      });
      setMessages(prev => {
        const next = { ...prev };
        for (const cv of convs) {
          // Do not clobber a grid-opened thread: this read would substitute the contact's OPEN conversation
          // for the conversation the operator actually tapped. The pinned one refreshes on its own below.
          if (waThreadRef.current?.visitorId === cv.id) continue;
          next[cv.id] = (cv.messages ?? []).map((m: any) => ({
            id: m.id,
            from: m.from === 'visitor' ? cv.id : 'agent',
            content: m.content,
            timestamp: m.timestamp,
          }));
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch WhatsApp conversations:', err);
    }
  };

  /**
   * WA-2 / AC-W10 — open the thread of the conversation that was TAPPED, addressed by conversation id.
   * 🔴 A pure read: GET only, nothing is written, so opening a row never takes it over or resolves it
   * (INV-1 / AC-W9's open-a-row leg). The thread view itself is untouched — this only fills the message map
   * and the selection it already renders from.
   */
  const loadWaThread = async (conversationId: string, select = true): Promise<boolean> => {
    try {
      const res = await fetch(`/api/whatsapp-grid/${encodeURIComponent(conversationId)}/thread`);
      if (!res.ok) return false;
      const th = await res.json();
      setMessages(prev => ({
        ...prev,
        [th.visitorId]: (th.messages ?? []).map((m: any) => ({
          id: m.id,
          from: m.from === 'visitor' ? th.visitorId : 'agent',
          content: m.content,
          timestamp: m.timestamp,
        })),
      }));
      setWaThread({ conversationId: th.conversationId, visitorId: th.visitorId });
      // A background refresh must not re-select: it would resurrect a chat the agent has since ended.
      if (!select) return true;
      // The reply target stays the VISITOR id (unchanged agent:chat contract); the conversation id only
      // decides WHICH messages are shown.
      setSelectedChat({
        id: th.visitorId,
        customer: th.customer,
        channel: 'whatsapp',
        humanManaged: th.humanManaged,
        status: th.status,
        waitTime: '',
        priority: 'medium',
        preview: th.messages?.length ? th.messages[th.messages.length - 1].content : '',
        unread: false,
        location: { country: '', city: '', phone: th.phone ?? '' },
        previousChats: [],
        activeCases: [],
      });
      return true;
    } catch (err) {
      console.error('[wa-grid] failed to open conversation:', err);
      return false;
    }
  };

  const handleOpenGridRow = (row: WaRow) => {
    if (row.channel === 'whatsapp' && row.conversationId) {
      void loadWaThread(row.conversationId);
      return;
    }
    setWaThread(null);
    const chat = chats.find(c => c.id === row.visitorId);
    if (chat) { setSelectedChat(chat); return; }

    /**
     * 🔴 A ROW THE OPERATOR CAN SEE MUST BE A ROW THE OPERATOR CAN OPEN.
     *
     * This used to be `chats.find(...)` and then nothing — so a web row sourced from the live roster
     * rather than from an accepted `chat:assigned` silently NO-OPPED on click. Surfacing conversations
     * with no way into them is barely better than hiding them, and it is worse in one respect: the
     * operator cannot tell a dead row from a slow one, so they click again.
     *
     * The row is promoted on CLICK rather than on arrival, deliberately. `chats` feeds the legacy
     * conversation-list panes too, so filling it up front put previews into a list that is
     * `display:none` below `lg` and broke two unrelated specs. Promoting here keeps that list meaning
     * what it has always meant — conversations this console is actually working — and costs nothing
     * until the operator asks for the thread.
     *
     * The messages come from the visitor's replayed `chatStatus`, which is the only copy the client
     * has for a chat it never received live events for.
     */
    const visitor = visitors.find(v => v.id === row.visitorId);
    if (!visitor) return;

    const promoted: Chat = {
      id: visitor.id,
      customer: row.contact || visitor.id.substring(0, 8),
      channel: 'chat',
      status: 'active',
      waitTime: '0m',
      priority: 'medium',
      preview: visitor.chatStatus?.lastMessage || row.preview || '',
      unread: false,
      location: {
        country: visitor.location?.country || 'Unknown',
        city: visitor.location?.city || 'Unknown',
        phone: 'N/A',
      },
      previousChats: [],
      activeCases: [],
    };

    const replayed = visitor.chatStatus?.messages;
    if (replayed?.length) {
      // Never clobber a local thread: anything already held is live state, including optimistic sends.
      setMessages(prev => (prev[visitor.id]?.length ? prev : { ...prev, [visitor.id]: replayed }));
    }
    setChats(prev => (prev.some(c => c.id === promoted.id) ? prev : [...prev, promoted]));
    setSelectedChat(promoted);
  };

  useEffect(() => {
    fetchWhatsAppConversations();
    const t = setInterval(fetchWhatsAppConversations, 15000);
    return () => clearInterval(t);
  }, []);

  // Keep the pinned (grid-opened) conversation fresh on the same cadence the inbox merge skips it on.
  useEffect(() => {
    if (!waThread) return;
    const id = waThread.conversationId;
    const t = setInterval(() => { void loadWaThread(id, false); }, 15000);
    return () => clearInterval(t);
  }, [waThread?.conversationId]);

  const handleSaveCustomer = async (visitorId: string) => {
    try {
      const res = await fetch(`/api/customers/${visitorId}/save`, { method: 'POST' });
      if (res.ok) {
        fetchCustomers();
        // Update local visitors state to reflect isCustomer change immediately
        setVisitors(prev => prev.map(v => v.id === visitorId ? { ...v, isCustomer: true } : v));
        // Force server to broadcast updated visitor status to all agents
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'visitor:refresh', visitorId }));
        }
      }
    } catch (err) {
      console.error('Failed to save customer:', err);
    }
  };

  // Remove a Lead from the rolodex (AC18): un-list + retain + suppress re-qualification.
  const handleRemoveLead = async (id: string) => {
    try {
      const res = await fetch(`/api/customers/${id}/remove`, { method: 'POST' });
      if (res.ok) fetchCustomers();
    } catch (err) {
      console.error('Failed to remove lead:', err);
    }
  };

  const handleProactiveChat = (visitorId: string, content: string) => {
    if (!socket || !user) return;

    const id = uuidv4();
    const timestamp = new Date().toISOString();

    socket.send(JSON.stringify({
      type: 'agent:proactive-chat',
      data: {
        visitorId,
        agentId: user.id,
        content,
        id,
        timestamp
      }
    }));

    // Add message to local state
    setMessages(prev => ({
      ...prev,
      [visitorId]: [
        ...(prev[visitorId] || []),
        {
          id,
          from: 'agent',
          content,
          timestamp
        }
      ]
    }));

    // We don't setActiveTab('conversations') here because the agent might want to stay on visitors tab
    // But we should ensure the chat is added to the chats list
    const visitor = visitors.find(v => v.id === visitorId);
    if (visitor) {
      const newChat: Chat = {
        id: visitor.id,
        customer: visitor.name || visitor.id.substring(0, 8),
        channel: 'chat',
        status: 'active',
        waitTime: '0m',
        priority: 'medium',
        preview: content,
        unread: false,
        location: {
          country: visitor.location?.country || 'Unknown',
          city: visitor.location?.city || 'Unknown',
          phone: 'N/A'
        },
        previousChats: [],
        activeCases: []
      };
      setChats(prev => {
        if (prev.find(c => c.id === newChat.id)) return prev;
        return [...prev, newChat];
      });
      setSelectedChat(newChat);
      setActiveTab('conversations');
    }
  };

  console.log('AgentConsole Render. Chats:', chats.length, 'Selected:', selectedChat?.id);

  // Remove any localStorage effects
  // Remove any mock data loading

  const userRef = useRef(user);
  const accountIdRef = useRef(accountId);

  useEffect(() => {
    userRef.current = user;
    accountIdRef.current = accountId;
  }, [user, accountId]);

  /**
   * 🔴 THE CONSOLE RECONNECTS. It used to open ONE socket for the lifetime of the page, with no `onclose`
   * and no retry, so the first time that socket died the console became a dead terminal until somebody
   * reloaded — and it never said so. The server reaps a socket after two missed pongs, so this is a normal
   * event, not an exotic one. An agent replying to a customer had their message silently discarded
   * (`WebSocket.send()` on a non-OPEN socket does not throw — it drops the frame and returns).
   *
   * Backoff is capped low on purpose: an agent staring at a customer's message is not willing to wait a
   * minute, and the reconnect is cheap. `agent:join` is re-sent by `onopen` on EVERY connect, so the server
   * re-associates the socket with the account — without that the server drops every later frame for having
   * no accountId, which is a silent failure that looks exactly like the one being fixed.
   */
  const wsRef = useRef<WebSocket | null>(null);
  const wsRetryRef = useRef(0);
  const wsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsClosedForGoodRef = useRef(false);

  useEffect(() => {
    const connect = () => {
      if (wsClosedForGoodRef.current) return;
    const wsUrl = window.location.origin.replace(/^http/, 'ws');
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Connected to server');
      wsRetryRef.current = 0;         // a successful connect resets the backoff
      setSendError(null);             // the transport is healthy again; stop warning about it
      // 🔴 RESYNC ON EVERY CONNECT, NOT JUST THE FIRST. This is the load-bearing line for the incident that
      // started all this: the agent socket was lost ~56s after joining, and when the web chat's events
      // fired there was no console listening — Socket count=0, no recipient, so the conversation never
      // entered the roster at all. It was never "buried"; it was never delivered. Real-time events are
      // fire-and-forget, so a console that was deaf for even a moment has a permanent hole in its list
      // unless something re-states the truth on reconnect. For WEB rows that is the `agent:join` below:
      // it makes the server replay the roster (`sendVisitorsToAgent`), and the `visitors:update` handler
      // hydrates the rows from it. WhatsApp needs its own read because it is not in the live roster.
      void fetchWhatsAppConversations();
      if (userRef.current) {
        ws.send(JSON.stringify({
          type: 'agent:join',
          agentId: userRef.current.id,
          accountId: accountIdRef.current
        }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('Agent received message:', message);

        switch (message.type) {
          case 'visitors:update': {
            setVisitors(prev => message.data.map((newV: Visitor) => {
              const existingV = prev.find(v => v.id === newV.id);

              // Map incoming data
              const mergedV = { ...newV };

              // If incoming has no chatStatus, but our local one has 'waiting', preserve it
              if (!newV.chatStatus && existingV?.chatStatus?.waiting) {
                mergedV.chatStatus = {
                  messages: existingV.chatStatus.messages || [],
                  inChat: false,
                  waiting: true,
                  unread: true,
                  lastMessage: existingV.chatStatus.lastMessage
                };
              } else if (newV.chatStatus && existingV?.chatStatus?.waiting && !newV.chatStatus.inChat) {
                // If incoming HAS chatStatus but it says waiting is false (server race), force true
                mergedV.chatStatus = { ...newV.chatStatus, waiting: true };
              }

              return mergedV;
            }));
            break;
          }

          case 'chat:message':
            setVisitors(prev => prev.map(v => {
              if (v.id === message.data.visitorId && !v.chatStatus?.inChat) {
                const existingMsgs = v.chatStatus?.messages || [];
                const newMessage = {
                  id: message.data.id,
                  from: message.data.from,
                  content: message.data.content,
                  timestamp: message.data.timestamp,
                  deliveryStatus: message.data.deliveryStatus
                };

                return {
                  ...v,
                  chatStatus: {
                    ...(v.chatStatus || { inChat: false, agentId: undefined }),
                    messages: [...existingMsgs, newMessage],
                    waiting: true,
                    unread: true,
                    lastMessage: message.data.content
                  }
                };
              }
              return v;
            }));

            setMessages(prev => {
              const vId = message.data.visitorId;
              const vMsgs = prev[vId] || [];
              if (vMsgs.some(m => m.id === message.data.id)) return prev;
              return {
                ...prev,
                [vId]: [...vMsgs, {
                  id: message.data.id,
                  from: message.data.from,
                  content: message.data.content,
                  timestamp: message.data.timestamp,
                  deliveryStatus: message.data.deliveryStatus
                }]
              };
            });
            break;

          // 🔴 (cmdr, operator-reported): startProactiveChat previously never told the SENDING agent
          // when live delivery didn't happen — the console had already optimistically painted the
          // message as sent. Reuses the same sendError banner handleSendMessage already shows for its
          // own client-side transport check. Two distinct reasons, two distinct messages: a
          // socket_not_open visitor is STILL buffered server-side and will self-deliver on their next
          // reconnect (replayAgentMessagesToVisitor) — that is the feature's normal "before they open
          // chat" case, not a failure, so it must not read as one. visitor_not_found has no buffer to
          // fall back on (no roster entry to attach it to) — a real, unrecoverable miss.
          case 'agent:proactive-chat:failed':
            setSendError(message.data?.reason === 'visitor_not_found' ? 'proactive-not-found' : 'proactive-queued');
            break;

          case 'chat:assigned':
            const assignedVisitor = message.data.visitor;
            const assignedAgentId = message.data.agentId;

            const newChat: Chat = {
              id: assignedVisitor.id,
              customer: assignedVisitor.id.substring(0, 8),
              channel: 'chat',
              status: 'active',
              waitTime: '0m',
              priority: 'medium',
              preview: assignedVisitor.chatStatus?.lastMessage || '',
              unread: true,
              location: {
                country: assignedVisitor.location?.country || 'Unknown',
                city: assignedVisitor.location?.city || 'Unknown',
                phone: 'N/A'
              },
              previousChats: [],
              activeCases: []
            };

            setChats(prev => {
              if (prev.find(c => c.id === newChat.id)) return prev;
              return [...prev, newChat];
            });

            if (assignedVisitor.chatStatus?.messages) {
              const msgs = assignedVisitor.chatStatus.messages;
              setMessages(prev => ({ ...prev, [assignedVisitor.id]: msgs }));
            }

            // Automatically select the chat if it was assigned to us
            if (userRef.current && assignedAgentId === userRef.current.id) {
              setSelectedChat(newChat);
              setActiveTab('conversations');
            }
            break;

          case 'chat:ended':
            setChats(prev => prev.filter(c => c.id !== message.data.visitorId));
            setSelectedChat(prev => prev?.id === message.data.visitorId ? null : prev);
            break;
        }
      } catch (error) {
        console.error('Error processing message:', error);
      }
    };

      ws.onerror = () => {
        // An error is always followed by a close; let onclose own the retry so it happens exactly once.
        console.warn('[ws] socket error — waiting for close to schedule a reconnect');
      };

      ws.onclose = () => {
        if (wsClosedForGoodRef.current) return;   // unmounting — not a failure, do not retry
        // 1s, 2s, 4s, 8s, capped at 15s. Capped low: an agent is mid-conversation with a customer.
        const delay = Math.min(1000 * Math.pow(2, wsRetryRef.current), 15000);
        wsRetryRef.current += 1;
        console.warn(`[ws] disconnected — reconnecting in ${delay}ms (attempt ${wsRetryRef.current})`);
        wsTimerRef.current = setTimeout(connect, delay);
      };

      setSocket(ws);
    };

    connect();

    return () => {
      wsClosedForGoodRef.current = true;
      if (wsTimerRef.current) clearTimeout(wsTimerRef.current);
      wsRef.current?.close();
    };
  }, []);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState<ChatSurvey>({
    status: 'resolved',
    description: ''
  });

  const [selectedVisitor, setSelectedVisitor] = useState<string | null>(null);
  const [viewingPastChat, setViewingPastChat] = useState<Chat['previousChats'][0] | null>(null);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedChat || !socket) return;

    // 🔴 THE FRAME ONLY LEAVES THE BROWSER IF THE SOCKET IS OPEN. `send()` does NOT throw on a CLOSED or
    // CLOSING socket — it discards the frame and returns normally — and it DOES throw on a CONNECTING one.
    // Without this check the code below cleared the composer and painted the message into the thread, so
    // the agent was told their reply had been sent when nothing had been transmitted, nothing persisted,
    // and nothing reached the customer. Keep the text where the agent can retry it, say so plainly, and
    // let the reconnect above restore the transport.
    if (socket.readyState !== WebSocket.OPEN) {
      console.warn('[ws] refusing to send on a non-OPEN socket (readyState=' + socket.readyState + ')');
      setSendError('not-sent');
      return;
    }

    const id = uuidv4();
    const timestamp = new Date().toISOString();

    if (!user) return;

    const message = {
      type: 'agent:chat',
      data: {
        id: id,
        agentId: user.id,
        visitorId: selectedChat.id,
        content: messageInput,
        timestamp: timestamp,
        accountId: accountId
      }
    };

    socket.send(JSON.stringify(message));
    setSendError(null);

    // Add message to local state
    setMessages(prev => ({
      ...prev,
      [selectedChat.id]: [
        ...(prev[selectedChat.id] || []),
        {
          id: id,
          from: 'agent',
          content: messageInput,
          timestamp: timestamp
        }
      ]
    }));

    setMessageInput('');
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'messenger':
        return <Facebook className="w-5 h-5 text-primary" />;
      case 'whatsapp':
        return <MessageSquare data-testid="channel-badge-whatsapp" className="w-5 h-5 text-green-600" />;
      case 'instagram':
        return <Instagram className="w-5 h-5 text-purple-600" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleEndChat = () => {
    setShowSurvey(true);
  };

  // T1 — one tap: resolve context server-side (account/contact + the real conversation id), THEN open
  // the create form pre-filled. No manual re-picking a link the agent already established by being in
  // this thread.
  const handleCreateTicketFromConversation = async () => {
    if (!selectedChat) return;
    try {
      const res = await fetch(`/api/tickets/context?visitorId=${encodeURIComponent(selectedChat.id)}`);
      if (!res.ok) return;
      const ctx = await res.json();
      setTicketCreatePrefill({
        conversationId: ctx.conversationId, companyId: ctx.companyId, leadId: ctx.leadId,
        unitId: ctx.unitId ?? null, unitCandidates: ctx.unitCandidates ?? [],
      });
      setThreadMenuOpen(false);
    } catch { /* leave the affordance visible, nothing changes on failure */ }
  };

  // L2 (D-L1) — hand a WhatsApp conversation back to the bot. Server clears agentId in place + records
  // resolvedAt (account-scoped by the session); the bot auto-answers the next inbound. Refetch so the
  // conversation drops its human-managed state in the console.
  const handleHandBackToBot = async () => {
    if (!selectedChat || selectedChat.channel !== 'whatsapp') return;
    try {
      const res = await fetch(`/api/whatsapp/conversations/${encodeURIComponent(selectedChat.id)}/release`, { method: 'POST' });
      if (!res.ok) return;
      setSelectedChat(prev => (prev ? { ...prev, humanManaged: false } : prev));
      await fetchWhatsAppConversations();
    } catch (err) {
      console.error('[whatsapp] hand-back failed:', err);
    }
  };

  const handleSurveySubmit = () => {
    if (!selectedChat) return;

    const endedChat: EndedChat = {
      endedAt: new Date().toISOString(),
      survey: surveyData
    };

    // Update the chats array with the new state
    const updatedChats = chats.map(chat =>
      chat.id === selectedChat.id
        ? {
          ...chat,
          ended: endedChat,
          previousChats: [
            ...chat.previousChats,
            {
              date: new Date().toLocaleDateString(),
              topic: surveyData.description,
              status: surveyData.status,
              messages: messages[selectedChat.id] || [],
              survey: surveyData
            }
          ]
        }
        : chat
    );

    // Update both state and localStorage
    setChats(updatedChats);

    // Send end-chat message to server
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'agent:end-chat',
        data: {
          visitorId: selectedChat.id,
          accountId: accountId,
          survey: surveyData
        }
      }));
    }

    // Update the selected chat to reflect changes
    setSelectedChat(updatedChats.find(chat => chat.id === selectedChat.id) || null);

    setShowSurvey(false);
    setSurveyData({ status: 'resolved', description: '' }); // Reset survey form
  };

  // Initialize rightPaneWidth from localStorage or use default
  const [rightPaneWidth, setRightPaneWidth] = useState(() => {
    const savedWidth = localStorage.getItem('rightPaneWidth');
    return savedWidth ? parseInt(savedWidth) : 320;
  });
  const [isResizing, setIsResizing] = useState(false);
  // D3: below lg the details pane is a full-screen view opened on demand, not a column stealing the thread
  const [showDetailsMobile, setShowDetailsMobile] = useState(false);
  const [threadMenuOpen, setThreadMenuOpen] = useState(false);
  // #62 next-phase Tickets T1/T4 (cmdr/UXeng, 2026-08-23) — "create a ticket FROM a conversation in
  // ONE TAP, context pre-filled" + "the conversation carries a marker that it is linked to a ticket."
  const [ticketCreatePrefill, setTicketCreatePrefill] = useState<{
    conversationId: string; companyId: string | null; leadId: string | null;
    unitId: string | null; unitCandidates: Array<{ id: string; label: string; propertyName: string }>;
  } | null>(null);
  const [linkedTickets, setLinkedTickets] = useState<Array<{ id: string; number: string; subject: string; status: string }>>([]);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('rightPaneWidth', rightPaneWidth.toString());
  }, [rightPaneWidth]);

  // Resize handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      // Set min and max width limits
      const clampedWidth = Math.min(Math.max(newWidth, 280), 600);
      setRightPaneWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const [selectedCustomer, setSelectedCustomer] = useState<Chat | null>(null);

  const fetchCustomerHistory = async (visitorId: string) => {
    try {
      const response = await fetch(`/api/history/${visitorId}`);
      if (response.ok) {
        const history = await response.json();
        const previousChats = history.map((session: any) => ({
          date: new Date(session.metadata.startTime).toLocaleDateString(),
          topic: session.metadata.survey?.description || (session.metadata.status === 'resolved' ? 'Resolved Ticket' : 'Conversation'),
          status: session.metadata.status,
          messages: session.messages,
          survey: session.metadata.survey || { status: session.metadata.status, description: 'Chat Session' }
        }));

        // Update all related states
        setChats(prev => prev.map(chat =>
          chat.id === visitorId ? { ...chat, previousChats } : chat
        ));

        setSelectedChat(prev => (prev?.id === visitorId) ? { ...prev, previousChats } : prev);
        setSelectedCustomer(prev => (prev?.id === visitorId) ? { ...prev, previousChats } : prev);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  useEffect(() => {
    if (selectedChat?.id) {
      fetchCustomerHistory(selectedChat.id);
    }
  }, [selectedChat?.id, selectedChat?.ended]);

  // T4 — refetch the linked-ticket marker whenever the selected thread changes, AND after T1 creates a
  // ticket for the CURRENTLY selected thread (selectedChat.id doesn't change on create, so relying on
  // the effect's own dependency alone would leave the marker stale until the agent picks a different
  // conversation — the "appears without a reload" claim needs an explicit refetch, not just the effect).
  const refetchLinkedTickets = (visitorId: string) => {
    fetch(`/api/visitors/${encodeURIComponent(visitorId)}/tickets`).then((r) => r.ok ? r.json() : []).then((rows) => {
      setLinkedTickets(rows ?? []);
    }).catch(() => {});
  };
  useEffect(() => {
    setTicketCreatePrefill(null);
    if (!selectedChat?.id) { setLinkedTickets([]); return; }
    refetchLinkedTickets(selectedChat.id);
  }, [selectedChat?.id]);

  useEffect(() => {
    if (selectedCustomer?.id) {
      fetchCustomerHistory(selectedCustomer.id);
    }
  }, [selectedCustomer?.id]);

  const [notes, setNotes] = useState<Note[]>([]);

  const fetchNotes = async (visitorId: string) => {
    try {
      const res = await fetch(`/api/visitors/${visitorId}/notes`);
      if (res.ok) {
        const data = await res.json();
        setNotes(data);
      }
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  };

  const handleAddNote = async (content: string) => {
    const visitorId = selectedChat?.id || selectedCustomer?.id;
    if (!visitorId) return {} as Note;

    try {
      const res = await fetch(`/api/visitors/${visitorId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      if (res.ok) {
        const newNote = await res.json();
        setNotes(prev => [newNote, ...prev]);
        return newNote;
      }
    } catch (err) {
      console.error('Failed to add note:', err);
    }
    return {} as Note;
  };

  const handleUpdateContact = async (contactInfo: any) => {
    const visitorId = selectedChat?.id || selectedCustomer?.id;
    if (!visitorId) return;

    try {
      const res = await fetch(`/api/visitors/${visitorId}/contact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactInfo)
      });
      if (res.ok) {
        setVisitors(prev => prev.map(v =>
          v.id === visitorId
            ? { ...v, ...contactInfo }
            : v
        ));
        // F2 FIX (open-pane immediacy): also update the durable-Lead source the OPEN pane reads for
        // an OFFLINE lead (it is NOT in the live `visitors` array). Match the row id OR any anchor
        // token. Without this, the agent's own save shows nothing and looks like it failed.
        setCustomers(prev => prev.map(c =>
          (c.id === visitorId || c.anchorTokens?.includes(visitorId))
            ? { ...c, ...contactInfo }
            : c
        ));
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
    }
  };

  const handleUpdateTags = async (tags: string[]) => {
    const visitorId = selectedChat?.id || selectedCustomer?.id;
    if (!visitorId) return;

    try {
      const res = await fetch(`/api/customers/${visitorId}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags })
      });
      if (res.ok) {
        setVisitors(prev => prev.map(v =>
          v.id === visitorId
            ? { ...v, tags }
            : v
        ));
        // F2 FIX (open-pane immediacy): update the durable-Lead source too, so an OFFLINE lead's tag
        // edit reflects in the open pane with no reload. Match row id OR any anchor token.
        setCustomers(prev => prev.map(c =>
          (c.id === visitorId || c.anchorTokens?.includes(visitorId))
            ? { ...c, tags }
            : c
        ));
      }
    } catch (err) {
      console.error('Failed to update tags:', err);
    }
  };

  useEffect(() => {
    const visitorId = selectedChat?.id || selectedCustomer?.id;
    if (visitorId) {
      fetchNotes(visitorId);
    } else {
      setNotes([]);
    }
  }, [selectedChat?.id, selectedCustomer?.id]);

  const handleSelectCustomer = (v: Visitor | null) => {
    // null = deselect (the CRM-grid's mobile "back" from the full-screen detail returns to the grid).
    if (!v) { setSelectedCustomer(null); setViewingPastChat(null); return; }
    // Map Visitor to Chat-like object for the UI
    const chatProxy: Chat = {
      id: v.id,
      customer: v.name || v.id.substring(0, 8),
      channel: 'chat',
      status: 'offline',
      waitTime: '0m',
      priority: 'low',
      preview: v.notes || '',
      unread: false,
      location: {
        country: v.location?.country || 'Unknown',
        city: v.location?.city || 'Unknown',
        phone: 'N/A'
      },
      previousChats: [],
      activeCases: []
    };
    setSelectedCustomer(chatProxy);
    setViewingPastChat(null); // Clear viewing state
  };

  // Add this new component for the Customers tab
  // CustomersTab is a MODULE-LEVEL component (hoisted) — see above. Defining it inline here
  // gave it a new identity per render, remounting the pane + form mid-edit (F2 regression).

  const handleTakeChat = (visitorId: string) => {
    if (!socket || !user) return;
    socket.send(JSON.stringify({
      type: 'agent:take-chat',
      data: {
        visitorId,
        agentId: user.id,
        accountId: accountId
      }
    }));
    setActiveTab('conversations');
  };

  // Unified nav: the SAME 4 sections, moved from top-tabs into the left rail + mobile bottom-bar
  // (DESIGN-SYSTEM §3, reskin-only; #62 Phase F groups them ENGAGE-first per UXeng/GTM — the CRM
  // additions land alongside these existing screens, not in place of them). Each item still carries
  // its original tab-* testid + setActiveTab, and the customers item preserves the fetchCustomers
  // side-effect — so the functional specs are unaffected.
  // 'customers' groups under SELL (Leads = this rolodex, re-skinned per #62 — not renamed here; the
  // Lead/Contact label question is D3, still open with the operator, and not eng's call to harden).
  // No SERVE/BILL group renders — no screens ship behind them this phase (GTM's phase-gating ruling:
  // no placeholders, no dead links).
  const navItems: UINavItem[] = [
    // #62 Phase 2 — today_dashboard ships this phase, ungrouped and first (the landing page, per the
    // proposed nav's "Biombo -> Today -> ENGAGE -> SELL..." order — not part of ENGAGE or SELL).
    { id: 'today', label: t('navigation.today'), icon: <LayoutDashboard className="w-5 h-5" />, testid: 'tab-today' },
    { id: 'visitors', label: t('navigation.visitors'), icon: <Globe className="w-5 h-5" />, testid: 'tab-visitors', group: t('navigation.groupEngage') },
    { id: 'conversations', label: t('navigation.conversations'), icon: <MessageSquare className="w-5 h-5" />, testid: 'tab-conversations', group: t('navigation.groupEngage') },
    { id: 'customers', label: t('navigation.customers'), icon: <User className="w-5 h-5" />, testid: 'tab-customers', group: t('navigation.groupSell') },
    // #62 Phase 2 — Contacts is `kind='contact'` on the SAME table as Leads (D3 shape (a)), a new
    // real screen this phase, so it renders now (not a placeholder — GTM's phase-gating is satisfied).
    { id: 'contacts', label: t('navigation.contacts'), icon: <Users className="w-5 h-5" />, testid: 'tab-contacts', group: t('navigation.groupSell') },
    { id: 'accounts', label: t('navigation.accounts'), icon: <Building2 className="w-5 h-5" />, testid: 'tab-accounts', group: t('navigation.groupSell') },
    { id: 'pipeline', label: t('navigation.pipeline'), icon: <TrendingUp className="w-5 h-5" />, testid: 'tab-pipeline', group: t('navigation.groupSell') },
    // #62 next-phase (cmdr/UXeng, 2026-08-23) — first of the remaining-6 greenfield builds. "Selling
    // ends in money" (UXeng §1): the SELL group's natural endpoint, right after Pipeline.
    { id: 'invoices', label: t('navigation.invoices'), icon: <Receipt className="w-5 h-5" />, testid: 'tab-invoices', group: t('navigation.groupSell') },
    // #62 next-phase (cmdr/UXeng, 2026-08-23) — #2 in priority ("closest to the existing loop", a
    // conversational CRM whose origin is WhatsApp), right after Invoices.
    { id: 'tickets', label: t('navigation.tickets'), icon: <Ticket className="w-5 h-5" />, testid: 'tab-tickets', group: t('navigation.groupSell') },
    // #62 next-phase (cmdr/UXeng, 2026-08-24) — #3 in priority, right after Tickets. Observation
    // metrics only — a target is one number + one period, no verdict/score fabricated.
    { id: 'goals', label: t('navigation.goals'), icon: <Target className="w-5 h-5" />, testid: 'tab-goals', group: t('navigation.groupSell') },
    // #62 next-phase (cmdr/UXeng, 2026-08-24) — #4 of the remaining-6, last in priority order. Phase-3
    // scoped per build-plan §6.3: no 'Shared With'/ACL surface (RBAC doesn't exist yet).
    { id: 'documents', label: t('navigation.documents'), icon: <FileText className="w-5 h-5" />, testid: 'tab-documents', group: t('navigation.groupSell') },
    { id: 'tasks', label: t('navigation.tasks'), icon: <ListChecks className="w-5 h-5" />, testid: 'tab-tasks', group: t('navigation.groupSell') },
    { id: 'mega', label: t('navigation.mega'), icon: <ExternalLink className="w-5 h-5" />, testid: 'tab-mega' },
    { id: 'portfolio', label: t('navigation.portfolio'), icon: <Home className="w-5 h-5" />, testid: 'tab-portfolio', group: t('navigation.groupProperties') },
    { id: 'settings', label: t('navigation.settings'), icon: <Settings className="w-5 h-5" />, testid: 'tab-settings' },
  ];
  // Mobile bottom-bar: PM's steady-state order (build plan §9.3), REVERTED to canon now that Today
  // ships this phase — the Phase-2 tripwire ("Phase 2 is not done until the mobile primary set is
  // Today · Conversations · Leads · More", bound explicitly so this revert wasn't left to memory).
  // Visitors — the Phase-F interim filler for Today's empty slot — moves to "More" alongside Settings.
  const mobilePrimary = [
    navItems.find((it) => it.id === 'today'),
    navItems.find((it) => it.id === 'conversations'),
    navItems.find((it) => it.id === 'customers'),
  ].filter((it): it is UINavItem => !!it);
  // #62 Phase 2 — Contacts is a real screen this phase (mobile ships with its screen, cmdr's rule), but
  // there is no free primary slot for it (Conversations/Leads/Visitors already fill 3+More). It goes
  // behind "More" alongside Settings rather than being unreachable on phone.
  const mobileOverflow = navItems.filter((it) => it.id === 'settings' || it.id === 'contacts' || it.id === 'accounts' || it.id === 'pipeline' || it.id === 'invoices' || it.id === 'tickets' || it.id === 'goals' || it.id === 'documents' || it.id === 'tasks' || it.id === 'visitors' || it.id === 'mega' || it.id === 'portfolio');
  const handleNavSelect = (id: string) => {
    // 'mega' is an open-in-new-tab action, not a pane — it must not become the active tab.
    if (id === 'mega') {
      window.open(resolveMegaUrl(window.location.hostname), '_blank', 'noopener,noreferrer');
      return;
    }
    setActiveTab(id as ActiveTab);
    if (id === 'customers') fetchCustomers('lead');
    if (id === 'contacts') fetchCustomers('contact');
  };

  return (
    <div className="flex h-screen bg-canvas text-text-strong">
      <NavRail
        items={navItems}
        activeId={activeTab}
        onSelect={handleNavSelect}
        brand={<Brand />}
      />
      <div className="flex flex-col flex-1 min-w-0">
      {/* C3-b tenant-degradation console (outage / usage-cap), persistent top-of-shell */}
      <InferenceStatusBanner />
      {/* Top Bar */}
      <div className="bg-surface border-b border-border-neutral">
        {/* Upper section with profile */}
        <div className="px-4 py-2 flex justify-end items-center">
          <div className="flex items-center gap-4">
            <LanguageSelector />

            {/* Profile section stays the same */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 p-2 hover:bg-canvas rounded-lg"
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center overflow-hidden">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-medium">
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  )}
                </div>
                <span className="text-gray-700">{user?.name || 'User'}</span>
                <ChevronDown className="w-4 h-4 text-text-muted" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border-neutral py-1 z-50">
                  <div className="px-4 py-2 border-b border-border-neutral">
                    <p className="text-sm font-medium">{user?.name || 'User'}</p>
                    <p className="text-xs text-text-muted truncate" title={user?.email}>{user?.email || 'No email'}</p>
                  </div>
                  <button
                    onClick={() => {
                      setActiveTab('settings');
                      setIsProfileOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-canvas text-left"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('navigation.settings')}
                  </button>
                  <div className="border-t border-border-neutral my-1"></div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-canvas text-left"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('navigation.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-auto pb-16 md:pb-0">
        {activeTab === 'visitors' ? (
          <VisitorsTab
            visitors={visitors}
            onTakeChat={handleTakeChat}
            onSaveCustomer={handleSaveCustomer}
            onProactiveChat={handleProactiveChat}
          />
        ) : activeTab === 'settings' ? (
          <SettingsTab />
        ) : activeTab === 'portfolio' ? (
          <PortfolioTab onConnectWhatsApp={() => setActiveTab('settings')} onNavigateToContacts={() => setActiveTab('contacts')} />
        ) : activeTab === 'accounts' ? (
          <AccountsTab onNavigateToPortfolio={() => setActiveTab('portfolio')} />
        ) : activeTab === 'pipeline' ? (
          <PipelineTab />
        ) : activeTab === 'invoices' ? (
          <InvoicesTab />
        ) : activeTab === 'tickets' ? (
          <TicketsTab />
        ) : activeTab === 'goals' ? (
          <GoalsBoard />
        ) : activeTab === 'documents' ? (
          <DocumentsBoard />
        ) : activeTab === 'tasks' ? (
          <TaskBoard />
        ) : activeTab === 'today' ? (
          <Dashboard onNavigate={setActiveTab} />
        ) : activeTab === 'conversations' ? (
          // Conversations Tab Layout
          <>
            {/* WA-2 CONVERSATIONS GRID — replaces the 256px chat list (ratified §0.0). The old list was
                recency-only with a hardcoded "Wait: 0m", a Priority badge that could never render, and four
                dead channel buttons. Same E-3 layout contract as the Leads pane: the grid is full-width and
                primary with nothing selected, and the thread takes over on <lg when a row is opened. */}
            <div className={`flex-1 min-w-0 bg-white border-e border-border-neutral overflow-hidden ${selectedChat ? 'hidden lg:flex lg:w-64 lg:flex-none' : 'flex'}`}>
              <WaConversationsGrid
                compact={!!selectedChat}
                selectedKey={waThread?.conversationId ?? selectedChat?.id ?? null}
                onOpen={handleOpenGridRow}
                onConnectWhatsApp={() => setActiveTab('settings')}
                webRows={webRows}
                currentUserId={user?.id ?? null}
              />
            </div>

            {/* Main chat area */}
            {/* 🔴 D2: with nothing selected the grid is FULL-WIDTH and primary — the same E-3 resolution the
                Leads pane already shipped (0506448f). Keeping the empty "Select a conversation" placeholder
                at `lg:flex` took half the width and pushed ESTADO/RESPONSABLE/ESPERA/VENTANA/OPERATIVO —
                the entire projection — behind a horizontal scroll. An empty panel is not worth the pane. */}
            <div data-testid="wa-thread-pane" className={`flex-1 flex-col min-w-0 ${selectedChat ? 'flex' : 'hidden'}`}>
              {selectedChat ? (
                <>
                  {/* 🔴 PHONE THREAD HEADER (UXeng's direction after the re-first-60s).
                      At 390px this header carried up to FIVE inline actions with no wrap, no scroller and
                      no menu, so End Chat sat off-screen and UNREACHABLE — a functional loss. The overflow
                      is pre-existing; D3 giving the thread its full width is what revealed it.
                      The basis for the fix: on a phone NONE of these five is the primary action — the
                      primary action is TYPING, and the composer already owns it. So below md the header
                      holds exactly three things: back, identity, and one overflow menu. Desktop keeps its
                      inline row; it was never rejected and has the room. */}
                  <div data-testid="wa-thread-header" className="relative p-4 bg-white border-b border-border-neutral">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center min-w-0">
                        {/* 🔴 lg, NOT md — deliberately a different breakpoint from the overflow menu below.
                            Back exists whenever the LIST is hidden (E-3 hides it below lg); the menu exists
                            whenever the HEADER is cramped (below md). Two different questions, two answers;
                            collapsing them to one number stranded the operator at 1000px with no way back. */}
                        <button data-testid="wa-back-to-grid" aria-label="Back to conversations"
                          className="lg:hidden me-1 inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-primary font-semibold"
                          onClick={() => { setSelectedChat(null); setWaThread(null); setShowDetailsMobile(false); setThreadMenuOpen(false); }}>&larr;</button>
                        {getChannelIcon(selectedChat.channel)}
                        <div className="ms-3 min-w-0">
                          {/* identity truncates on one line — a long name must never wrap the header open */}
                          <h3 data-testid="wa-thread-title" className="font-semibold truncate">{selectedChat.customer}</h3>
                          <p className="text-sm text-gray-600 truncate">
                            {/* the brand is "WhatsApp": capitalising the channel key gave the wrong casing
                                for the one channel with a capital in the middle. */}
                            Via {selectedChat.channel === 'whatsapp' ? 'WhatsApp' : selectedChat.channel.charAt(0).toUpperCase() + selectedChat.channel.slice(1)}
                          </p>
                        </div>
                        {/* T4 — "the conversation carries a marker that it is linked to a ticket," so the
                            agent working the thread knows one exists without asking. */}
                        {linkedTickets.length > 0 && (
                          <span data-testid="wa-linked-ticket-marker" title={linkedTickets.map((tk) => tk.number).join(', ')}
                            className="ms-2 shrink-0 inline-flex items-center h-6 px-2 rounded-full bg-canvas text-text-muted text-[11px] font-semibold">
                            {t('ticketsGrid.linkedMarker', { count: linkedTickets.length })}
                          </span>
                        )}
                      </div>

                      {/* DESKTOP — the inline row, unchanged in content. Hand-back loses its solid green:
                          a filled green outweighed the blue End Chat, which put the loudest treatment on the
                          SAFER reversible action while the terminal one was quieter. */}
                      <div className="hidden md:flex items-center space-x-3 shrink-0">
                        {selectedChat.channel === 'whatsapp' && selectedChat.humanManaged && (
                          <button onClick={handleHandBackToBot} data-testid="whatsapp-hand-back"
                            className="px-3 py-2 text-sm rounded-lg border border-primary/40 text-primary hover:bg-primary/5">
                            Hand back to bot
                          </button>
                        )}
                        <button className="px-3 py-2 text-sm text-gray-700 hover:bg-canvas rounded-lg">View History</button>
                        <button className="px-3 py-2 text-sm text-gray-700 hover:bg-canvas rounded-lg">Transfer</button>
                        <button onClick={handleCreateTicketFromConversation} data-testid="wa-create-ticket"
                          className="px-3 py-2 text-sm rounded-lg border border-primary/40 text-primary hover:bg-primary/5">
                          {t('ticketsGrid.createFromConversation')}
                        </button>
                        <button onClick={handleEndChat} className="px-3 py-2 text-sm text-white bg-primary hover:bg-primary-dark rounded-lg">End Chat</button>
                      </div>

                      {/* PHONE — one menu owning all five. */}
                      <button data-testid="wa-thread-menu" aria-label="Conversation actions" aria-haspopup="menu"
                        aria-expanded={threadMenuOpen}
                        className="md:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-text-strong hover:bg-canvas shrink-0"
                        onClick={() => setThreadMenuOpen((o) => !o)}>&#8942;</button>
                    </div>

                    {threadMenuOpen && (
                      <>
                        <div className="md:hidden fixed inset-0 z-40" onClick={() => setThreadMenuOpen(false)} />
                        {/* anchored to the header's inline-END and clamped inside it, so the menu itself
                            cannot overflow the viewport — reachability is the whole point of this fix. */}
                        <div role="menu" data-testid="wa-thread-menu-panel"
                          className="md:hidden absolute z-50 top-full [inset-inline-end:0.75rem] mt-1 min-w-[12rem] max-w-[calc(100vw-1.5rem)] rounded-lg border border-border-neutral bg-surface shadow-lg py-1">
                          {selectedChat.channel === 'whatsapp' && selectedChat.humanManaged && (
                            <button role="menuitem" data-testid="whatsapp-hand-back"
                              className="w-full text-start px-4 min-h-[44px] text-sm text-text-strong hover:bg-canvas"
                              onClick={() => { setThreadMenuOpen(false); void handleHandBackToBot(); }}>Hand back to bot</button>
                          )}
                          <button role="menuitem" className="w-full text-start px-4 min-h-[44px] text-sm text-text-strong hover:bg-canvas">View History</button>
                          <button role="menuitem" className="w-full text-start px-4 min-h-[44px] text-sm text-text-strong hover:bg-canvas">Transfer</button>
                          <button role="menuitem" data-testid="wa-open-details"
                            className="w-full text-start px-4 min-h-[44px] text-sm text-text-strong hover:bg-canvas"
                            onClick={() => { setThreadMenuOpen(false); setShowDetailsMobile(true); }}>Details</button>
                          <button role="menuitem" data-testid="wa-create-ticket-mobile"
                            className="w-full text-start px-4 min-h-[44px] text-sm text-text-strong hover:bg-canvas"
                            onClick={handleCreateTicketFromConversation}>{t('ticketsGrid.createFromConversation')}</button>
                          {/* End Chat LAST, behind a divider, in destructive TEXT treatment — it is the one
                              terminal action here and must not read as the default. */}
                          <div className="my-1 border-t border-border-neutral" />
                          <button role="menuitem"
                            className="w-full text-start px-4 min-h-[44px] text-sm font-semibold text-opt-rose hover:bg-canvas"
                            onClick={() => { setThreadMenuOpen(false); handleEndChat(); }}>End Chat</button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Chat messages */}
                  <div className="flex-1 p-4 overflow-y-auto bg-canvas">
                    <div className="flex flex-col space-y-4">
                      {messages[selectedChat.id]?.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.from === 'agent' || message.from === 'agent-1' ? 'justify-end' : 'justify-start'
                            }`}   /* justify-end/start are LOGICAL in flex row — they follow the writing direction */
                        >
                          {/* 🔴 MF-2 — BLUE IS THE AGENT'S VOICE. The customer's message rendered in the
                              Send-button blue while ours rendered white, inverting the most basic signal on
                              a surface whose whole job is "who needs a reply". Inbound: neutral surface with
                              a hairline border. Outbound: primary. Direction is carried by alignment, which
                              uses logical properties, so it mirrors correctly in he-RTL. */}
                          <div className="max-w-xs flex flex-col">
                            <div
                              data-testid="chat-message"
                              data-wa-msg={message.from === 'agent' || message.from === 'agent-1' ? 'outbound' : 'inbound'}
                              className={`px-4 py-2 rounded-lg ${message.from === 'agent' || message.from === 'agent-1'
                                ? 'bg-primary text-white'
                                : 'bg-surface text-text-strong border border-border-neutral shadow-sm'
                                }`}
                            >
                              <p className="text-sm">{message.content}</p>
                            </div>
                            {/* 🔴 Generated + persisted ≠ delivered (wa-delivery incident). This reply was
                                shown here regardless of the WhatsApp send outcome; a refused/error result
                                must read as distinctly NOT sent, not as an ordinary message. */}
                            {message.deliveryStatus && message.deliveryStatus !== 'sent' && (
                              <div data-testid="chat-message-not-delivered" role="status" className="flex items-center gap-1 mt-1 text-[11px] text-opt-rose">
                                <span aria-hidden="true">⚠</span>
                                <span>{t('console.whatsappNotDelivered', 'Not delivered to WhatsApp')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Message input */}
                  <div className="p-4 bg-white border-t border-border-neutral">
                    {selectedChat.ended ? (
                      <div className="text-center text-red-500 py-2">
                        Chat ended
                      </div>
                    ) : (
                      <div className="flex flex-col space-y-2">
                        {/* 🔴 The agent must never be left believing a message went out when it did not.
                            Their text stays in the box above, so the retry is one click, not a retype. */}
                        {sendError && (
                          <div
                            data-testid="send-error"
                            role="status"
                            className="flex items-center gap-2 text-sm text-opt-rose"
                          >
                            <span aria-hidden="true">⚠</span>
                            <span>{sendError === 'proactive-queued'
                              ? t('console.proactiveQueued', "Not delivered yet — their connection wasn't open. Queued: it will reach them automatically once they're back on the page.")
                              : sendError === 'proactive-not-found'
                              ? t('console.proactiveNotFound', 'Not delivered — this visitor is no longer on the page.')
                              : t('console.sendFailed', 'Not sent — reconnecting. Your message is still here; press Send again in a moment.')}</span>
                          </div>
                        )}
                      <div className="flex space-x-3">
                        <input
                          type="text"
                          data-testid="message-input"
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Type your message..."
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                        />
                        <button
                          onClick={handleSendMessage}
                          data-testid="send-button"
                          className="px-4 py-2 text-white bg-primary hover:bg-primary-dark rounded-lg min-h-[44px]"
                        >
                          Send
                        </button>
                      </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-canvas">
                  <div className="text-center text-text-muted">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3" />
                    <p>Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Customer Information */}
            {selectedChat && (
              <>
                {/* 🔴 D3 — the resizer is a DESKTOP control (it needs a mouse drag) and it took 4px from a
                    390px phone. Hidden below lg with the pane it resizes. */}
                <div
                  className="hidden md:block w-1 bg-gray-200 hover:bg-primary cursor-col-resize active:bg-primary transition-colors"
                  onMouseDown={() => setIsResizing(true)}
                />

                {/* Breakpoint is md: (768), not lg:. The defect is a PHONE defect — measured at 390 and
                    375 — and every device from a tablet up has room for the three-pane layout. Taking the
                    pane away at lg: would have been a wider change than the bug, and it regressed
                    lead_enrichment at 1000px, which legitimately expects the sidebar. */}
                {/* 🔴 D3 — CustomerDetails kept a FIXED 320px inline width with no responsive behaviour, so
                    opening a conversation on a 390px phone left the thread 66px wide (51px at 375). That is
                    not a narrow thread, it is an unusable one, and it is the first thing he meets after
                    tapping a row. Below lg the pane leaves the row entirely and opens FULL-SCREEN on demand
                    — the same E-3 treatment the Leads pane already uses — so the thread gets the phone and
                    the details are still one tap away. The inline width applies only from lg up. */}
                <div
                  data-testid="wa-details-pane"
                  className={`bg-canvas border-s border-border-neutral overflow-hidden flex-col md:flex md:w-[var(--rp-width)] ${showDetailsMobile ? 'flex fixed inset-0 z-40 md:static md:inset-auto' : 'hidden'}`}
                  // the resizer's px value rides in a CSS VAR so the responsive decision stays in CSS —
                  // a JS `window.innerWidth` read here would be stale until the next render, which is
                  // exactly the kind of "correct once, wrong after a rotate" bug this pane already had.
                  style={{ ['--rp-width' as any]: `${rightPaneWidth}px` }}
                >
                  {showDetailsMobile && (
                    <button data-testid="wa-details-back" className="md:hidden self-start flex items-center gap-1 px-4 py-3 text-sm text-primary font-semibold"
                      onClick={() => setShowDetailsMobile(false)}>&larr; Back</button>
                  )}
                  <CustomerDetails
                    // ONE SUBJECT (PRD §1 / design §2): personId is the stable identity token, resolved
                    // server-side by Lead identity (GET /api/customers/:id/detail → resolveLeadId) —
                    // never by live-roster presence. `visitor` is OPTIONAL live enrichment ONLY (undefined
                    // once the tab closes / the visitor leaves the live roster); the durable record no
                    // longer depends on it, which is what let an offline lead render blank before (F1/F2).
                    personId={selectedChat.id}
                    visitor={visitors.find(v => v.id === selectedChat.id)}
                    chat={selectedChat}
                    onViewPastChat={setViewingPastChat}
                    onUpdateContact={handleUpdateContact}
                    onUpdateTags={handleUpdateTags}
                    onAddNote={handleAddNote}
                    notes={notes}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          // Customers/Contacts Tab Layout — same shell, `kind` picks the grid + the /api/customers filter.
          <CustomersTab
            kind={activeTab === 'contacts' ? 'contact' : 'lead'}
            customers={customers}
            onSelectCustomer={handleSelectCustomer}
            selectedCustomer={selectedCustomer}
            onViewPastChat={setViewingPastChat}
            onRemoveLead={handleRemoveLead}
            visitors={visitors}
            onUpdateContact={handleUpdateContact}
            onUpdateTags={handleUpdateTags}
            onAddNote={handleAddNote}
            notes={notes}
            onContactCreated={handleContactCreated}
          />
        )}
      </div>

      {viewingPastChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[600px] h-[600px] flex flex-col">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <div>
                <h2 className="text-lg font-semibold">{viewingPastChat.topic}</h2>
                <p className="text-sm text-text-muted">{viewingPastChat.date} - {viewingPastChat.status}</p>
              </div>
              <button onClick={() => setViewingPastChat(null)} className="text-text-muted hover:text-gray-700">
                <LogOut className="w-5 h-5 transform rotate-180" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-canvas p-4 rounded-lg mb-4">
              <div className="space-y-4">
                {viewingPastChat.messages.map((message, index) => (
                  <div key={index} className={`flex ${message.from !== 'visitor' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      data-testid="chat-message"
                      className={`max-w-[70%] p-3 rounded-lg ${message.from !== 'visitor' ? 'bg-primary text-white' : 'bg-white shadow'
                        }`}>
                      <p className="text-sm">{message.content}</p>
                      <span className="text-xs opacity-75 block mt-1">
                        {new Date(message.timestamp || '').toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {viewingPastChat.survey && (
              <div className="bg-canvas p-4 rounded-lg">
                <h3 className="text-sm font-semibold mb-2">Survey Results</h3>
                <div className="text-sm">
                  <p><span className="font-medium">Status:</span> {viewingPastChat.survey.status}</p>
                  <p><span className="font-medium">Description:</span> {viewingPastChat.survey.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showSurvey && selectedChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-lg font-semibold mb-4">End Chat Survey</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chat Status
                </label>
                <select
                  value={surveyData.status}
                  onChange={(e) => setSurveyData(prev => ({
                    ...prev,
                    status: e.target.value as ChatSurvey['status']
                  }))}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="resolved">Resolved</option>
                  <option value="pending">Pending</option>
                  <option value="escalated">Escalated</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={surveyData.description}
                  onChange={(e) => setSurveyData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  className="w-full p-2 border border-gray-300 rounded-lg h-32"
                  placeholder="Enter chat description..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSurvey(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-canvas rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSurveySubmit}
                  className="px-4 py-2 text-white bg-primary hover:bg-primary-dark rounded-lg"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* T1 — one tap from the conversation, context already resolved. Modal overlay (not the tab's own
          side-panel slot — that belongs to CustomerDetails here), same z-50 layer as the survey modal. */}
      {ticketCreatePrefill && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50" data-testid="ticket-create-from-conversation-overlay">
          <div className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto">
            <TicketDetailPanel
              ticketId="new"
              sheetShape={typeof window !== 'undefined' && window.innerWidth < 768 ? 'sheet' : 'popover'}
              prefill={ticketCreatePrefill}
              onClose={() => setTicketCreatePrefill(null)}
              onCreated={() => { setTicketCreatePrefill(null); if (selectedChat?.id) refetchLinkedTickets(selectedChat.id); }}
              onUpdated={() => {}}
            />
          </div>
        </div>
      )}
      <BottomBar items={mobilePrimary} overflow={mobileOverflow} activeId={activeTab} onSelect={handleNavSelect} moreLabel={t('navigation.more')} />
      </div>
    </div>
  );
};

export default AgentConsole;